import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import Button from "@/components/Button";
import { Form, Table } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useSessionStorage } from "usehooks-ts";
import {
  DocumentType,
  type MiscContent,
  MiscContentType,
  PropoundingParty,
} from "@/briefpoint-client";
import { DropDownFilterList } from "@/components/DropDownFilterList";
import { DropDownRadio } from "@/components/DropDownRadio";
import Loading from "@/components/Loading";
import { useContentApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { DOCUMENT_SUB_TYPES } from "@/lib/constants";
import type { DocumentSubType } from "@/lib/types";
import {
  caseTypeDisplay,
  getCaseTypeDisplay,
  getSortedTags,
  getTagsDisplay,
  getTagsKeys,
} from "@/lib/utils";
import styles from "./QuestionsTable.module.scss";
import QuestionTableRow from "./QuestionTableRow";

export interface GroupedContentWithExpanded {
  [key: string]: {
    questions: MiscContent[];
    expanded?: boolean;
  };
}

export default function QuestionsTable() {
  const contentApi = useContentApi();
  const tagApi = useTagApi();
  const history = useHistory();

  const [caseFilter, setCaseFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<string | null>(null);
  const [positionFilter, setPositionFilter] = useState<string | null>(null);
  const [showDefaults, setShowDefaults] = useSessionStorage(
    "library-show-bp-defaults",
    false,
  );

  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const { jurisdictions } = useAuth();
  const californiaCourt = (jurisdictions ?? [])[0];
  const [groupedQuestions, setGroupedQuestions] =
    useState<GroupedContentWithExpanded>({});

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const documentTypeParam = searchParams.get("document") ?? "1";

  const tagsQuery = useQuery({
    queryKey: ["tagApi.tagFindTags"],
    queryFn: () => tagApi.tagFindTags({}),
  });

  const defaultQuestionQuery = useQuery({
    queryKey: [
      "contentApi.contentGetDefaultMiscContentFiltered",
      DocumentType.DiscoveryRequest,
      MiscContentType.DiscoveryQuestion,
      Number(documentTypeParam),
      true,
    ],
    queryFn: () =>
      contentApi.contentGetDefaultMiscContentFiltered({
        documentType: DocumentType.DiscoveryRequest,
        type: MiscContentType.DiscoveryQuestion,
        documentSubType: Number(documentTypeParam),
        skipTagFiltering: true,
      }),
  });

  const questionsQuery = useQuery({
    queryKey: [
      "contentApi.contentGetAllMiscContent",
      DocumentType.DiscoveryRequest,
      MiscContentType.DiscoveryQuestion,
    ],
    queryFn: () =>
      contentApi.contentGetAllMiscContent({
        documentType: DocumentType.DiscoveryRequest,
        type: MiscContentType.DiscoveryQuestion,
      }),
  });

  const availableTags = new Map(
    tagsQuery.data?.map((tag) => [tag.id, tag.name ?? ""]),
  );

  /* biome-ignore lint/correctness/useExhaustiveDependencies: only run when specific dependencies change */
  useEffect(() => {
    /* wait for all queries to complete */
    if (
      questionsQuery.isLoading ||
      tagsQuery.isLoading ||
      defaultQuestionQuery.isLoading
    )
      return;

    let questionsData = questionsQuery.data ?? [];
    const defaultQuestionsData = defaultQuestionQuery.data ?? [];

    /* if show library is checked, add default questions to the questions data */
    if (showDefaults) {
      questionsData = questionsData.concat(defaultQuestionsData);
    }

    // filter by document sub type (document search param)
    questionsData = questionsData.filter(
      (x) => x.documentSubType === Number(documentTypeParam),
    );

    // filter by case type
    if (caseFilter.length > 0) {
      questionsData = questionsData?.filter((tq) =>
        caseFilter.some((cf) => tq.caseTypeId === cf),
      );
    }

    // filter by tags
    if (tagFilter.length > 0) {
      questionsData = questionsData?.filter((questionItem) =>
        tagFilter.some((tagFilter) =>
          questionItem.tagIds?.some((tagId) => tagId === Number(tagFilter)),
        ),
      );
    }

    //filter by position
    if (positionFilter !== null) {
      questionsData = questionsData.filter((questionItem) => {
        if (
          (positionFilter === "Plaintiff" &&
            questionItem.respondentType === PropoundingParty.Defendant) ||
          (positionFilter === "Defendant" &&
            questionItem.respondentType === PropoundingParty.Plaintiff)
        ) {
          return true;
        }

        return false;
      });
    }

    // filter by individual/class
    if (classFilter !== null) {
      questionsData = questionsData.filter((questionItem) => {
        if (
          (classFilter === "Individual" &&
            questionItem.isForRepresentative === false) ||
          (classFilter === "Representative" &&
            questionItem.isForRepresentative === true)
        ) {
          return true;
        }
        return false;
      });
    }

    // group by question data
    const questionsGrouped = questionsData?.reduce(
      (newGroupedQuestions, question) => {
        /* create unique key using data from the object */
        const questionKey = `${caseTypeDisplay(question.caseTypeId, californiaCourt, true)}
                    ${getTagsKeys(question.tagIds, availableTags)}
                    ${question.isForRepresentative !== undefined ? `-${question.isForRepresentative}` : ""}
                    ${question.respondentType !== undefined ? `-${question.respondentType}` : ""}
                    ${!!question.sharedWithFirmId && "-firm"}${!question.ownerId && "-bp"}`;

        /* if there are no questions, default expanded state to false otherwise check if the group is already expanded or use the allExpanded boolean */
        const isExpanded =
          Object.keys(groupedQuestions).length === 0
            ? false
            : (groupedQuestions[questionKey]?.expanded ?? allExpanded);

        /* create new array if key doesn't exist */
        if (!newGroupedQuestions[questionKey]) {
          newGroupedQuestions[questionKey] = {
            questions: [],
            expanded: isExpanded,
          };
        }
        /* add object to array */
        newGroupedQuestions[questionKey].questions.push(question);
        return newGroupedQuestions;
      },
      {} as GroupedContentWithExpanded,
    );

    setGroupedQuestions(questionsGrouped ?? {});
  }, [
    defaultQuestionQuery.data,
    defaultQuestionQuery.isLoading,
    questionsQuery.data,
    questionsQuery.isLoading,
    tagsQuery.data,
    tagsQuery.isLoading,
    caseFilter,
    tagFilter,
    positionFilter,
    classFilter,
    showDefaults,
    documentTypeParam,
  ]);

  const hasFilters =
    caseFilter.length ||
    tagFilter.length ||
    positionFilter !== null ||
    classFilter !== null;

  const classFilterOptions = [
    { name: "Any", id: null, default: true },
    { name: "Individual", id: "Individual" },
    { name: "Representative", id: "Representative" },
  ];

  const positionFilterOptions = [
    { name: "Any", id: null, default: true },
    { name: "Plaintiff", id: "Plaintiff" },
    { name: "Defendant", id: "Defendant" },
  ];

  const caseFilterText = caseFilter.length
    ? getCaseTypeDisplay(caseFilter, californiaCourt)
    : "Case Type";

  const tagsFilterText = tagFilter.length
    ? getTagsDisplay(tagFilter, availableTags)
    : "Tags";

  function handleChangeSelectedSubType(subType: DocumentSubType) {
    searchParams.set("document", subType.id.toString());
    history.replace({
      search: searchParams.toString(),
    });
  }

  function setExpanded(id: string, expanded: boolean) {
    const newGroupedQuestions = { ...groupedQuestions };
    newGroupedQuestions[id] = { ...newGroupedQuestions[id], expanded };
    setGroupedQuestions(newGroupedQuestions);
  }

  function handleAddQuestion() {
    history.push({
      pathname: "/library/addeditquestion",
      search: `?type=${documentTypeParam}`,
    });
  }

  const allExpanded = Object.values(groupedQuestions).every(
    (group) => group.expanded,
  );

  function expandAllQuestions() {
    const newGroupedQuestions = { ...groupedQuestions };
    Object.values(newGroupedQuestions).forEach((group) => {
      group.expanded = !allExpanded;
    });
    setGroupedQuestions(newGroupedQuestions);
  }

  function clearAllFilters() {
    setCaseFilter([]);
    setTagFilter([]);
    setPositionFilter(null);
    setClassFilter(null);
  }

  function getCaseTypes() {
    const caseTypes = new Set<string>();
    Object.values(groupedQuestions).forEach((group) => {
      group.questions.forEach((question) => {
        if (question.caseTypeId) {
          caseTypes.add(question.caseTypeId);
        }
      });
    });
    return Array.from(caseTypes)
      .map((caseTypeId) => ({
        name: caseTypeDisplay(caseTypeId, californiaCourt, true) ?? "",
        id: caseTypeId,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  const findTags = useMemo(() => {
    const data =
      showDefaults && defaultQuestionQuery.data
        ? questionsQuery.data?.concat(defaultQuestionQuery.data)
        : questionsQuery.data;
    return getSortedTags(data ?? [], availableTags, tagFilter);
  }, [
    questionsQuery.data,
    availableTags,
    tagFilter,
    showDefaults,
    defaultQuestionQuery.data,
  ]);

  return (
    <>
      {/* Left side menu */}
      <div className={styles.container}>
        <div className={styles.subTypesContainer}>
          {DOCUMENT_SUB_TYPES.map((documentSubType: DocumentSubType) => (
            <Button
              variant="link"
              key={documentSubType.id}
              onClick={() => {
                handleChangeSelectedSubType(documentSubType);
              }}
              className={clsx(styles.subTypeButton, {
                [styles.subTypeButtonSelected]:
                  documentSubType.id === Number(documentTypeParam),
              })}
            >
              {documentSubType.name}
            </Button>
          ))}
        </div>
        {/* Right side content */}
        <div className={styles.contentContainer}>
          <Loading
            isLoading={
              questionsQuery.isLoading ||
              tagsQuery.isLoading ||
              defaultQuestionQuery.isLoading
            }
          >
            {/* Right side actions header */}
            <div className={styles.libraryControlsRow}>
              <div className={styles.libraryFiltersRow}>
                <div
                  className={styles.libraryFiltersCol}
                  style={{ minWidth: "650px" }}
                >
                  <div className={styles.filtersWrapper}>
                    <b className={styles.filterLabelHeader}>Filter By:</b>
                    <div className={styles.filtersGroup}>
                      <div className={styles.filterDropdownWrapper}>
                        {!!caseFilter.length && (
                          <label
                            className={styles.filterLabel}
                            htmlFor="case-type-filter"
                          >
                            Case Type
                          </label>
                        )}
                        <DropDownFilterList
                          key="case-type-filter"
                          text={caseFilterText}
                          itemsList={getCaseTypes()}
                          activeFilter={caseFilter}
                          updateFilter={setCaseFilter}
                        />
                      </div>
                      <div className={styles.filterDropdownWrapper}>
                        {!!tagFilter.length && (
                          <label
                            className={styles.filterLabel}
                            htmlFor="tags-filter"
                          >
                            Tags
                          </label>
                        )}
                        <DropDownFilterList
                          key="tags-filter"
                          text={tagsFilterText}
                          itemsList={findTags}
                          activeFilter={tagFilter}
                          updateFilter={setTagFilter}
                        />
                      </div>
                      <div className={styles.filterDropdownWrapper}>
                        {positionFilter !== null && (
                          <label
                            className={styles.filterLabel}
                            htmlFor="position-filter"
                          >
                            Your Position
                          </label>
                        )}
                        <DropDownRadio
                          key="position-filter"
                          id="position-filter"
                          text="Your Position"
                          itemsList={positionFilterOptions}
                          activeFilter={positionFilter}
                          updateFilter={setPositionFilter}
                        />
                      </div>
                      <div className={styles.filterDropdownWrapper}>
                        {classFilter !== null && (
                          <label
                            className={styles.filterLabel}
                            htmlFor="class-filter"
                          >
                            Individual/Class
                          </label>
                        )}
                        <DropDownRadio
                          key="class-filter"
                          id="class-filter"
                          text="Individual/Class"
                          itemsList={classFilterOptions}
                          activeFilter={classFilter}
                          updateFilter={setClassFilter}
                        />
                      </div>
                      {hasFilters && (
                        <Button
                          className={`${styles.libraryClearBtn} clear-btn`}
                          variant="link"
                          onClick={clearAllFilters}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.libraryButtonCol}>
                  <div className={styles.libraryCheckboxWrapper}>
                    <Form.Check
                      className={styles.formCheck}
                      id="include-defaults-check"
                      checked={showDefaults}
                      onChange={(e) => setShowDefaults(e.target.checked)}
                    />
                    <label
                      htmlFor="include-defaults-check"
                      className={styles.formCheckLabel}
                    >
                      Show Briefpoint Library
                    </label>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleAddQuestion}
                    className={styles.libraryCreateButton}
                  >
                    Create New
                  </Button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className={styles.tableResponsiveWrapper}>
              <Table striped hover>
                <thead>
                  <tr className={styles.tableHeader}>
                    <th />
                    <th className={styles.headerCell}>Case Type</th>
                    <th className={styles.headerCell}>Tags</th>
                    <th className={styles.headerCell}>Resp. Position</th>
                    <th className={styles.headerCell}>Representative</th>
                    <th className={styles.headerCell}># of Questions</th>
                    <th className={clsx(styles.headerCell, styles.actions)}>
                      <Button
                        className={styles.expandAllButton}
                        variant="link"
                        onClick={expandAllQuestions}
                      >
                        {allExpanded ? "Collapse All" : "Expand All"}
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(groupedQuestions ?? {}).length === 0 && (
                    <tr>
                      <td colSpan={7} className="pt-3 text-center">
                        No questions found
                      </td>
                    </tr>
                  )}
                  {Object.keys(groupedQuestions ?? {})
                    .sort((a, b) => a.localeCompare(b))
                    .map((questionKey, idx) => {
                      return [
                        <QuestionTableRow
                          key={idx}
                          questionKey={questionKey}
                          availableTags={availableTags}
                          groupedQuestions={groupedQuestions}
                          setExpanded={setExpanded}
                        />,
                      ];
                    })}
                </tbody>
              </Table>
            </div>
          </Loading>
        </div>
      </div>
    </>
  );
}
